import { memo, useEffect, useMemo } from 'react';

import { Elements } from '@stripe/react-stripe-js';
import { Stripe, type StripeElementsOptions } from '@stripe/stripe-js';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { getEnvVars } from '@order/utils';

import { type StripeElementsProviderProps } from '../../types';
import { getMinimumAmount, loadStripeWithRetry } from '../../utils';

export const StripeElementsProvider = memo((props: StripeElementsProviderProps) => {
  const { children, amount = 0, sessionId, onFailedToLoadStripe } = props;

  const stripePaymentMethodConfig = useFeatureFlag('permanent-stripe-payment-method-config'); // prettier-ignore
  const paymentMethodConfiguration = stripePaymentMethodConfig?.defaultId;

  const stripePromise = useMemo(() => loadStripeWithRetry(publicKey), []);
  useStripeLoadMonitor(stripePromise, onFailedToLoadStripe);

  const elementsStripe = useMemo(() => stripePromise.catch(() => null), [stripePromise]);

  const options = useMemo<StripeElementsOptions>(
    () => ({
      amount: getMinimumAmount(amount),
      currency,
      mode,
      paymentMethodCreation,
      captureMethod,
      customerSessionClientSecret: sessionId,
      paymentMethodConfiguration,
    }),
    [amount, sessionId, paymentMethodConfiguration],
  );

  return (
    <Elements stripe={elementsStripe} options={options}>
      {children}
    </Elements>
  );
});

const useStripeLoadMonitor = (
  stripePromise: Promise<Stripe | null>,
  onFailedToLoadStripe?: () => void,
) => {
  useEffect(() => {
    let isMounted = true;

    stripePromise.catch(() => {
      if (isMounted) {
        onFailedToLoadStripe?.();
      }
    });

    return () => {
      isMounted = false;
    };
  }, [stripePromise, onFailedToLoadStripe]);
};

// ─── Constants ───────────────────────────────────────────────────────────────

const currency = 'usd';
const mode = 'payment';
const paymentMethodCreation = 'manual';
const captureMethod = 'manual';
const publicKey = getEnvVars().STRIPE_PUBLIC_KEY;
